import React, { useState } from 'react';
import { Shield, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { authApi } from '../services';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TwoFactorLogin = ({ email, password, onSuccess, onBack }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (code.length < 6) {
      setError('Koden må være minst 6 tegn');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login/2fa`, {
        email,
        password,
        code
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ugyldig kode');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-blue-100 text-blue-600 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Shield className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            To-faktor Verifisering
          </h2>
          <p className="text-gray-600">
            {useBackupCode 
              ? 'Skriv inn en av backup-kodene dine'
              : 'Skriv inn 6-sifret kode fra autentiseringsappen din'
            }
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {useBackupCode ? 'Backup-kode' : 'Autentiseringskode'}
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => {
                if (useBackupCode) {
                  // Allow backup code format: XXXX-XXXX
                  setCode(e.target.value.toUpperCase().slice(0, 9));
                } else {
                  // Only digits, max 6
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                }
              }}
              placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
              className="w-full text-center text-2xl font-mono tracking-widest p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
              autoFocus
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || code.length < 6}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifiserer...' : 'Bekreft'}
          </button>

          {/* Toggle Backup Code */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setUseBackupCode(!useBackupCode);
                setCode('');
                setError('');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {useBackupCode 
                ? 'Bruk autentiseringsapp i stedet'
                : 'Bruk backup-kode'
              }
            </button>
          </div>

          {/* Back Button */}
          <button
            type="button"
            onClick={onBack}
            className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-700 py-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Tilbake til innlogging
          </button>
        </form>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Tips:</strong> Åpne autentiseringsappen din (Google Authenticator, 
            Microsoft Authenticator, eller Authy) og skriv inn den 6-sifrede koden.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorLogin;
